import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { checkPermission } from "server/security/utils";
import { EmptyList } from "server/components";
import { administrationRequests } from "api/administrationApi";
import { doctoralRequests } from "api/doctoralApi";
import {
  DoctorantStatusChoices,
  DoctorantStatusColorMap,
  DoctoralStudyTypeChoices,
} from "utils/choices";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import StatusChip from "../Partials/StatusChip";
import { instructionLanguageLabel, studyTypeLabel } from "../seatLabel";
import DoctoralDateField from "../Partials/DoctoralDateField";

const filters = [
  <TextInput label="Име" source="name" style={{ width: 200 }} alwaysOn />,
  <TextInput label="Имейл" source="email" style={{ width: 200 }} alwaysOn />,
  <ReferenceInput
    source="structuralUnitId"
    reference={administrationRequests.getStructuralUnitsSelect()}
    label="Факултет"
    alwaysOn
  >
    <SelectInput label="Факултет" emptyText="Всички" style={{ minWidth: 240 }} />
  </ReferenceInput>,
  <ReferenceInput
    source="departmentId"
    reference={administrationRequests.getDepartmentsSelect()}
    label="Катедра"
    alwaysOn
  >
    <SelectInput label="Катедра" emptyText="Всички" style={{ minWidth: 240 }} />
  </ReferenceInput>,
  <ReferenceInput
    source="doctoralProgramId"
    reference={doctoralRequests.getDoctoralProgramsSelect()}
    label="Докторантска програма"
    alwaysOn
  >
    <AutocompleteInput
      label="Докторантска програма"
      optionText={record => record?.name ?? ""}
      style={{ minWidth: 280 }}
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
    />
  </ReferenceInput>,
  <SelectInput
    source="studyTypeId"
    label="Форма на обучение"
    choices={DoctoralStudyTypeChoices}
    emptyText="Всички"
    style={{ width: 200 }}
    alwaysOn
  />,
];

const ListDoctorants = () => {
  const userPermissions = getPermissions();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const ListActions = hasCreate => (
    <TopToolbar>{hasCreate && <CreateButton variant="contained" />}</TopToolbar>
  );

  return (
    <List
      title="Докторанти"
      filters={filters}
      empty={<EmptyList />}
      exporter={false}
      actions={<ListActions hasCreate={checkPermission(userPermissions, permissions.canAddDoctorant)} />}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "ID", value: record?.id }]} />
              <MobileListRow fields={[{ label: "Име", value: record?.fullName ?? "—" }]} />
            </div>
          )}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Имейл", value: record?.email ?? "—" }]} />
              <MobileListRow fields={[{ label: "Факултет", value: record?.structuralUnitName ?? "—" }]} />
              <MobileListRow fields={[{ label: "Катедра", value: record?.departmentName ?? "—" }]} />
              <MobileListRow
                fields={[{ label: "Докторантска програма", value: record?.doctoralProgramNameBg }]}
              />
              <MobileListRow fields={[{ label: "Форма", value: studyTypeLabel(record.studyTypeId) }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Език",
                    value: instructionLanguageLabel(record.instructionLanguageId),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: (
                      <StatusChip
                        status={record.status}
                        choices={DoctorantStatusChoices}
                        colorMap={DoctorantStatusColorMap}
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Записан на",
                    value: <DoctoralDateField source="enrolledOn" sortable={false} />,
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={() => <ShowButton label="" />}
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <TextField source="id" label="ID" sortable={false} />
          <TextField source="fullName" label="Име" emptyText="—" sortable={false} />
          <TextField source="email" label="Имейл" emptyText="—" sortable={false} />
          <TextField source="structuralUnitName" label="Факултет" emptyText="—" sortable={false} />
          <TextField source="departmentName" label="Катедра" emptyText="—" sortable={false} />
          <TextField source="doctoralProgramNameBg" label="Докторантска програма" sortable={false} />
          <FunctionField label="Форма" render={record => studyTypeLabel(record.studyTypeId)} />
          <FunctionField
            label="Език"
            render={record => instructionLanguageLabel(record.instructionLanguageId)}
          />
          <FunctionField
            label="Статус"
            render={record => (
              <StatusChip
                status={record.status}
                choices={DoctorantStatusChoices}
                colorMap={DoctorantStatusColorMap}
              />
            )}
          />
          <DoctoralDateField source="enrolledOn" label="Записан на" sortable={false} />
          <ShowButton label="Детайли" />
        </Datagrid>
      )}
    </List>
  );
};

export default ListDoctorants;
